import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

const API_BASE = "https://api.gabrielny.com:8080/ai_dashboard.php";
const API_GET = `${API_BASE}?query=natquery_all`;

const MAP_LABELS = {
  attCategory: "Category",
  attCollection: "Collection",
  itemGrade: "Grade",
  attMaterial: "Material",
  attMetal: "Metal",
  attOccasion: "Occasion",
  attStyle: "Style",
  attDiamondQuality: "Diamond Quality",
  attDivision: "Division",
  attStoneColorQuality: "Stone Color Quality",
};

const MAPPING_OPTIONS = Object.entries(MAP_LABELS).map(([key, label]) => ({
  key,
  label,
}));

function humanizeKey(k = "") {
  const withoutAtt = k.startsWith("att") ? k.slice(3) : k;
  const spaced = withoutAtt.replace(/([A-Z])/g, " $1").trim();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : k;
}

function parseRuleValue(raw) {
  if (!raw) return { key: "", values: [] };
  const cleaned = String(raw).replace(/\\/g, "").trim();
  const pair = cleaned.match(/^([A-Za-z_][\w]*)\s*:\s*(.+)$/);
  if (pair) {
    const key = pair[1];
    let val = pair[2].trim();
    if (val.startsWith("[") && val.endsWith("]")) val = val.slice(1, -1).trim();
    const values = val ? val.split(",").map(s => s.trim()).filter(Boolean) : [];
    return { key, values };
  }
  const keyOnly = cleaned.match(/^([A-Za-z_][\w]*)$/);
  if (keyOnly) return { key: keyOnly[1], values: [] };
  return { key: "", values: [cleaned] };
}

function formatRuleValue({ key, values }) {
  const vs = (values || []).filter(Boolean);
  if (key && vs.length === 0) return key;
  if (!key) return vs[0] ?? "";
  return vs.length > 1 ? `${key}: [${vs.join(", ")}]` : `${key}: ${vs[0] ?? ""}`;
}


async function postQuery(payload) {
  const res = await fetch(API_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  try {
    return await res.json();
  } catch {
    return ;
  }
}

const PenIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm18-11.06c0 .4-.16.78-.44 1.06l-2.12 2.12-3.75-3.75 2.12-2.12a1.5 1.5 0 0 1 2.12 0l1.63 1.63c.28.28.44.66.44 1.06z"/>
  </svg>
);

const TrashIcon = (props) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...props}>
    <path d="M9 3h6a1 1 0 0 1 1 1v1h5v2H3V5h5V4a1 1 0 0 1 1-1zm-3 6h12l-1 12a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L6 9z"/>
  </svg>
);

function ModalShell({ open, onRequestClose, title, children }) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onRequestClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        fontFamily: "inherit",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          width: 520,
          maxWidth: "92vw",
          padding: 20,
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
          fontFamily: "inherit",
        }}
      >
        {title ? <h3 style={{ marginTop: 0 }}>{title}</h3> : null}
        {children}
      </div>
    </div>
  );
}

function AddPopup({ open, onRequestClose, onSuccess }) {
  const [form, setForm] = useState({ word: "", mappingKey: "", mappingValuesText: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setResult(null);

    const values = form.mappingValuesText.split(",").map((s) => s.trim()).filter(Boolean);
    const ruleValue = formatRuleValue({ key: form.mappingKey, values :[] });

    try {
      const data = await postQuery({
        query: "natquery_save",
        ruleKey: form.word,
        ruleValue,
        user_id: 101,
        ruleType: 1
      });
      setResult(data);
      onSuccess && onSuccess(data);
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell open={open} onRequestClose={onRequestClose} title="">
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <label>
          Reference Word
          <input
            value={form.word}
            onChange={(e) => setForm((f) => ({ ...f, word: e.target.value }))}
            required
            style={inputStyle}
          />
        </label>

        <label>
          refers to attribute
          <select
            value={form.mappingKey}
            onChange={(e) => setForm((f) => ({ ...f, mappingKey: e.target.value }))}
            required
            style={selectStyle}
          >
            <option value="" disabled>
              Select…
            </option>
            {MAPPING_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

       

        {error && <div style={{ color: "crimson" }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onRequestClose} disabled={submitting} style={btnGhost}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} style={btnPrimary}>
            {submitting ? "Submitting..." : "Submit"}
          </button>
        </div>

        {result && (
          <pre style={{ background: "#f7f7f7", padding: 12, marginTop: 8 }}>
            {JSON.stringify(result, null, 2)}
          </pre>
        )}
      </form>
    </ModalShell>
  );
}

function EditRowModal({ row, onClose, onSaved }) {
  const open = !!row;
  const [form, setForm] = useState({ ruleKey: "", mappingKey: "", mappingValuesText: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!row) return;
    const { key, values } = parseRuleValue(row.ruleValue ?? "");
    setForm({
      ruleKey: row.ruleKey ?? "",
      mappingKey: key ?? "",
      mappingValuesText: (values || []).join(", "),
    });
  }, [row]);

  const submit = async (e) => {
    e.preventDefault();
    if (!row?.id) return;
    setSubmitting(true);
    setError("");

    const values = form.mappingValuesText.split(",").map((s) => s.trim()).filter(Boolean);
    const ruleValue = formatRuleValue({ key: form.mappingKey, values : []});

    try {
      await postQuery({
        query: "natquery_save",
        id: row.id,
        ruleKey: form.ruleKey,
        ruleValue,
        user_id: 101,
        ruleType: 1
      });
      onSaved && onSaved();
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell open={open} onRequestClose={onClose} title="">
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <label>
          Reference Word
          <input
            value={form.ruleKey}
            onChange={(e) => setForm((f) => ({ ...f, ruleKey: e.target.value }))}
            required
            style={inputStyle}
          />
        </label>

        <label>
          refers to attribute
          <select
            value={form.mappingKey}
            onChange={(e) => setForm((f) => ({ ...f, mappingKey: e.target.value }))}
            required
            style={selectStyle}
          >
            <option value="" disabled>
              Select…
            </option>
            {MAPPING_OPTIONS.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        {error && <div style={{ color: "crimson" }}>{error}</div>}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" onClick={onClose} disabled={submitting} style={btnGhost}>
            Cancel
          </button>
          <button type="submit" disabled={submitting} style={btnPrimary}>
            {submitting ? "Updating..." : "Update"}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteConfirmModal({ row, onClose, onDeleted }) {
  const open = !!row;
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onDelete = async () => {
    if (!row?.id) return;
    setSubmitting(true);
    setError("");
    try {
      await postQuery({ query: "natquery_delete", id: row.id });
      onDeleted && onDeleted();
    } catch (err) {
      setError(err.message || "Request failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell open={open} onRequestClose={onClose} title="Delete Mapping">
      <p style={{ marginTop: 0 }}>
        Are you sure you want to delete <strong>{row?.ruleKey}</strong>?
      </p>
      {error && <div style={{ color: "crimson", margin: "10px 0" }}>{error}</div>}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" onClick={onClose} disabled={submitting} style={btnGhost}>
          Cancel
        </button>
        <button
          type="button"
          onClick={onDelete}
          disabled={submitting}
          style={{
            background: "#FEF2F2",
            color: "#B91C1C",
            border: "1px solid #FCA5A5",
            padding: "6px 10px",
            borderRadius: 6,
            cursor: submitting ? "not-allowed" : "pointer",
          }}
        >
          {submitting ? "Deleting..." : "Delete"}
        </button>
      </div>
    </ModalShell>
  );
}

const columnHelper = createColumnHelper();

function Reference() {
  const [data, setData] = useState(null);
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);

  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState([{ id: "ruleKey", desc: false }]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const searchRef = useRef(null);

  const fetchData = async () => {
    setLoading(true);
    setApiError("");
    try {
      const res = await fetch(API_GET);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setPageIndex(0);
    } catch (e) {
      setApiError(e.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const handler = (e) => {
      if (e.keyCode === 114 || (e.ctrlKey && e.key.toLowerCase() === "f")) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const rows = useMemo(() => {
    const arr = Array.isArray(data) ? data : data ? [data] : [];
    return arr.filter((r) => Number(r?.ruleType) === 1);
  }, [data]);

  const filteredRows = useMemo(() => {
    if (!debouncedSearch) return rows;
    return rows.filter((r) => {
      const a = `${r?.ruleKey ?? ""}`.toLowerCase();
      const { key, values } = parseRuleValue(r?.ruleValue ?? "");
      const label = MAP_LABELS[key] || humanizeKey(key);
      const joined = [key, label, ...values].join(" ").toLowerCase();
      return a.includes(debouncedSearch) || joined.includes(debouncedSearch);
    });
  }, [rows, debouncedSearch]);

  const Highlight = ({ text, query }) => {
    if (!query) return <>{text}</>;
    const q = query.toLowerCase();
    const str = String(text ?? "");
    const out = [];
    let i = 0;
    for (;;) {
      const idx = str.toLowerCase().indexOf(q, i);
      if (idx === -1) {
        out.push(str.slice(i));
        break;
      }
      out.push(str.slice(i, idx));
      out.push(<mark key={idx}>{str.slice(idx, idx + q.length)}</mark>);
      i = idx + q.length;
    }
    return <>{out}</>;
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("ruleKey", {
        id: "ruleKey",
        header: () => (
          <span style={{ fontSize: 14, fontWeight: 700, color: "#4B5563", paddingLeft: "10px" }}>
            Reference Word
          </span>
        ),
        cell: (info) => (
          <span style={{ fontSize: 14, fontWeight: 600, paddingLeft: "10px" }}>
            <Highlight text={info.getValue()} query={debouncedSearch} />
          </span>
        ),
      }),
      columnHelper.accessor("ruleValue", {
        id: "ruleValue",
        header: () => <span style={{ fontSize: 14, fontWeight: 700, color: "#4B5563" }}>Attribute</span>,
        cell: (info) => {
          const { key, values } = parseRuleValue(info.getValue());
          const label = key ? MAP_LABELS[key] || humanizeKey(key) : "Mapping";
          return (
            <div style={{ fontSize: 14 }}>
              <div style={{ fontWeight: 700, color: "#374151", marginBottom: 6 }}>{label}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {values.length ? (
                  values.map((v, i) => (
                    <span
                      key={i}
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: 999,
                        background: "#EEF2FF",
                        border: "1px solid #E0E7FF",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#4338CA",
                      }}
                    >
                      {v}
                    </span>
                  ))
                ) : (
                  <span style={{ color: "#6B7280" }}>—</span>
                )}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        header: () => <span style={{ fontSize: 14, fontWeight: 700, color: "#4B5563" }}>Actions</span>,
        cell: (info) => {
          const row = info.row.original;
          const disabled = !row?.id;
          return (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                title="Edit mapping"
                onClick={() => !disabled && setEditingRow(row)}
                disabled={disabled}
                style={btnIcon}
              >
                <PenIcon />
              </button>
              <button
                title="Delete mapping"
                onClick={() => !disabled && setDeletingRow(row)}
                disabled={disabled}
                style={btnDanger}
              >
                <TrashIcon />
              </button>
            </div>
          );
        },
      }),
    ],
    [debouncedSearch]
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting, pagination: { pageIndex, pageSize } },
    onSortingChange: setSorting,
    onPaginationChange: (updater) => {
      const next = typeof updater === "function" ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex);
      setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div style={{ fontFamily: "Arial", padding: 16 }}>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <button
          onClick={() => setShowAdd(true)}
          style={btnPrimary}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#7A3FFF")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#601EF9")}
        >
          ＋ Add Reference
        </button>
      </div>


      <div style={{ padding: 8 }}>
        <input
          ref={searchRef}
          style={{
            width: "100%",
            padding: 12,
            background: "#F9FAFB",
            borderRadius: 8,
            border: "1px solid #9036E3",
          }}
          type="text"
          placeholder="Search word, type, or value… (Ctrl+F)"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPageIndex(0);
          }}
        />
      </div>


      {apiError && (
        <div style={{ color: "#B91C1C", background: "#FEF2F2", border: "1px solid #FCA5A5", padding: 12, borderRadius: 8, marginBottom: 8 }}>
          {apiError}
        </div>
      )}


      <div style={{ marginBottom: 8, fontSize: 12, color: "#4B5563" }}>
        Showing{" "}
        <strong>
          {filteredRows.length ? table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1 : 0}-
          {Math.min(
            (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
            filteredRows.length
          )}
        </strong>{" "}
        of <strong>{filteredRows.length}</strong>
      </div>


      <div style={{ marginTop: 12, overflowX: "auto" }}>
        <table style={{ width: "100%" }}>
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} style={{ borderBottom: "2px solid #E5E7EB" }}>
                {hg.headers.map((header) => {
                  const isSorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      onClick={header.column.getToggleSortingHandler()}
                      style={{
                        cursor: header.id !== "actions" ? "pointer" : "default",
                        userSelect: "none",
                        borderBottom: "1px solid #E5E7EB",
                        paddingTop: 16,
                        paddingBottom: 8,
                        paddingRight: 16,
                        textAlign: "left",
                      }}
                      title={header.id !== "actions" ? "Click to sort" : undefined}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.id !== "actions" && (
                          <span style={{ fontSize: 12 }}>
                            {isSorted === "asc" ? "▲" : isSorted === "desc" ? "▼" : ""}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} style={{ padding: 24, textAlign: "center", fontSize: 14 }}>
                  Loading…
                </td>
              </tr>
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row, i) => (
                <tr key={row.id} style={{ background: i % 2 ? "#F9FAFB" : "white" }}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      style={{
                        minWidth: 180,
                        paddingTop: 12,
                        paddingBottom: 12,
                        paddingRight: 160,
                        verticalAlign: "top",
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} style={{ padding: 24, textAlign: "center", fontSize: 14, color: "#6B7280" }}>
                  {data ? "No results match your search." : "No data."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>


      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 8,
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          marginTop: 12,
        }}
      >
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage()}>
            « First
          </button>
          <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
            ‹ Prev
          </button>
        </div>

        <span style={{ fontSize: 14 }}>
          Page <strong>{table.getState().pagination.pageIndex + 1}</strong> / {table.getPageCount() || 1}
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>Go to:</span>
          <input
            type="number"
            min={1}
            value={table.getState().pagination.pageIndex + 1}
            onChange={(e) => {
              const page = e.target.value ? Number(e.target.value) - 1 : 0;
              table.setPageIndex(page);
            }}
            style={{ border: "1px solid #E5E7EB", borderRadius: 6, width: 64, textAlign: "center", padding: "4px 6px" }}
          />
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            style={{ border: "1px solid #E5E7EB", borderRadius: 6, padding: "4px 6px" }}
          >
            {[10, 25, 50, 100].map((ps) => (
              <option key={ps} value={ps}>
                Show {ps}
              </option>
            ))}
          </select>
          <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            Next ›
          </button>
          <button onClick={() => table.lastPage()} disabled={!table.getCanNextPage()}>
            Last »
          </button>
        </div>
      </div>

      <hr style={{ margin: "20px 0" }} />


      <AddPopup
        open={showAdd}
        onRequestClose={() => setShowAdd(false)}
        onSuccess={() => {
          setShowAdd(false);
          fetchData();
        }}
      />
      <EditRowModal
        row={editingRow}
        onClose={() => setEditingRow(null)}
        onSaved={() => {
          setEditingRow(null);
          fetchData();
        }}
      />
      <DeleteConfirmModal
        row={deletingRow}
        onClose={() => setDeletingRow(null)}
        onDeleted={() => {
          setDeletingRow(null);
          fetchData();
        }}
      />
    </div>
  );
}

const inputStyle = {
  background: "#fff",
  color: "#111827",
  border: "1px solid #601EF9",
  padding: "8px 14px",
  borderRadius: 10,
  fontWeight: 400,
  width: "100%",
};

const selectStyle = {
  background: "#fff",
  color: "#111827",
  border: "1px solid #601EF9",
  padding: "8px 14px",
  borderRadius: 10,
  fontWeight: 400,
  width: "100%",
  cursor: "pointer",
};

const btnPrimary = {
  background: "#601EF9",
  color: "white",
  border: "1px solid #4F46E5",
  padding: "8px 14px",
  borderRadius: 8,
  fontWeight: 600,
  cursor: "pointer",
  boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
  transition: "background 0.2s ease-in-out",
};

const btnGhost = {
  background: "transparent",
  color: "#374151",
  border: "1px solid #4F46E5",
  padding: "8px 14px",
  borderRadius: 10,
  fontWeight: 600,
  cursor: "pointer",
};

const btnIcon = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #E5E7EB",
  background: "#fff",
  cursor: "pointer",
};

const btnDanger = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "6px 10px",
  borderRadius: 6,
  border: "1px solid #FCA5A5",
  background: "#FEF2F2",
  color: "#B91C1C",
  cursor: "pointer",
};

export default Reference;
